// Branch dashboard - /Home/BranchDashboard. Design: dashboard-single-branch.html.
//
// Figures come from the sales report (one row per invoice), product-wise sale
// and the pay in / out report, for the branch picked in the app header. Blocks
// the server cannot answer yet keep their place in the layout and say "needs
// backend" - see docs/BACKEND-COMPLETE.md (Part 8).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  BarTrend,
  Card,
  DashScreen,
  DataTable,
  Delta,
  Drawer,
  Hero,
  MiniBtn,
  PERIODS,
  PeriodBar,
  Pending,
  Queue,
  Seg,
  Sparkline,
  StackBar,
  Tile,
  Todo,
  cap,
  cmpRange,
  compact,
  dailyTotals,
  dayLabel,
  downloadCsv,
  fetchInvoiceSpans,
  fetchItemSales,
  fetchPayInOut,
  fmtPct,
  hourLbl,
  hourWindow,
  hourlyTotals,
  inRange,
  initials,
  longDate,
  money,
  moneyDp,
  paceCut,
  payName,
  paymentMix,
  pct,
  rangeFor,
  rankBy,
  sparkRange,
  sumOf,
  useDashKeys,
  useToast,
  useUpdatedLabel,
  valueOf,
} from "../../components/dashboard/ui";

const BRANCH_PERIODS = PERIODS.filter((p) => p.id !== "qtd");
const ITEM_SORTS = [
  { id: "value", label: "By value" },
  { id: "qty", label: "By qty" },
  { id: "margin", label: "By profit" },
];
const clock = (mins) => (mins === null ? "" : `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`);

const INVOICE_COLS = [
  { k: "bill", t: "Bill no" },
  { k: "when", t: "Date / time" },
  { k: "who", t: "Cashier" },
  { k: "pay", t: "Payment" },
  { k: "amt", t: "Net total", num: true },
];
const invoiceRow = (r) => ({
  bill: r.billNo || "—",
  when: dayLabel(r.date) + (r.mins !== null ? " " + clock(r.mins) : ""),
  who: r.cashier || "—",
  pay: payName(r.payType),
  amt: moneyDp(r.net),
});

const Dashboard = () => {
  const { branch } = useSelector((state) => state.branchlist);
  const currfmt = useSelector((state) => {
    const match = state.usersetting?.settingList?.currencyFormat?.toString()?.match(/\.0+/);
    return match ? match[0].length - 1 : 2;
  });
  const guid = branch?.guid;

  const [period, setPeriod] = useState("today");
  const [cmp, setCmp] = useState("prev");
  // on by default so the headline matches the Sales report's Net Total
  const [vatOn, setVatOn] = useState(true);
  const [itemSort, setItemSort] = useState("value");
  const [tables, setTables] = useState({});
  const [drawer, setDrawer] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stamp, setStamp] = useState(null);
  const [reload, setReload] = useState(0);
  const [toast, showToast] = useToast();
  const updated = useUpdatedLabel(stamp);
  const req = useRef(0);

  /* ---------------- load ---------------- */
  useEffect(() => {
    if (!guid) return;
    const id = ++req.current;
    const r = rangeFor(period);
    const c = cmpRange(r, cmp);
    const sp = sparkRange(r);
    setLoading(true);
    setError("");
    Promise.all([
      fetchInvoiceSpans(branch, [r, c, sp]),
      fetchItemSales(branch, r.from, r.to).catch(() => []),
      fetchPayInOut(branch, r.from, r.to).catch(() => []),
    ])
      .then(([inv, items, pio]) => {
        if (id !== req.current) return;
        setData({ r, c, sp, inv, items, pio });
        setStamp(Date.now());
      })
      .catch((e) => {
        if (id !== req.current) return;
        console.error("Dashboard load failed", e?.response?.data?.Message || e);
        setError("Could not load the sales figures. Check the connection and press Refresh.");
      })
      .finally(() => id === req.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid, period, cmp, reload]);

  /* ---------------- derived ---------------- */
  const v = useMemo(() => {
    if (!data) return null;
    const { r, c, sp, inv, items, pio } = data;
    const f = valueOf(vatOn);
    const cur = inRange(inv, r.from, r.to);
    const prev = paceCut(inRange(inv, c.from, c.to), c.to, r.live);
    const sales = sumOf(cur, f);
    const psales = sumOf(prev, f);
    const payIn = sumOf(pio.filter((p) => !p.out), (p) => p.amount);
    const payOut = sumOf(pio.filter((p) => p.out), (p) => p.amount);
    const cashSales = sumOf(cur, (x) => x.cash);
    const days = (from, to, g) => dailyTotals(inv, from, to, g).map((d) => d.v);
    return {
      r,
      c,
      cur,
      prev,
      items,
      pio,
      f,
      sales,
      psales,
      inv: cur.length,
      pinv: prev.length,
      cashSales,
      payIn,
      payOut,
      cashHand: cashSales + payIn - payOut,
      pcash: sumOf(prev, (x) => x.cash),
      spark: days(sp.from, sp.to, f),
      sparkInv: days(sp.from, sp.to, () => 1),
      sparkCash: days(sp.from, sp.to, (x) => x.cash),
      firstMins: cur.reduce((m, x) => (x.mins !== null && (m === null || x.mins < m) ? x.mins : m), null),
    };
  }, [data, vatOn]);

  /* ---------------- trend ---------------- */
  const trend = useMemo(() => {
    if (!v) return null;
    const { r, c, cur, prev, f } = v;
    if (r.n === 1) {
      const win = hourWindow([cur, prev], r.live);
      if (win) {
        const a = hourlyTotals(cur, win, f);
        const b = hourlyTotals(prev, win, f);
        return {
          unit: "hour",
          x: a.map((o) => hourLbl(o.h)),
          cur: a.map((o) => o.v),
          cmp: b.map((o) => o.v),
          extra: a.map((o) => o.n),
          cmpName: c.name + (r.live ? ", to this hour" : ""),
        };
      }
      // the API sent bare dates - fall back to the last 12 days
      const sp = sparkRange(r);
      const d = dailyTotals(data.inv, sp.from, sp.to, f);
      const n = dailyTotals(data.inv, sp.from, sp.to, () => 1);
      return {
        unit: "day",
        noTimes: true,
        x: d.map((o) => dayLabel(o.date)),
        cur: d.map((o) => o.v),
        cmp: [],
        extra: n.map((o) => o.v),
        cmpName: c.name,
      };
    }
    const a = dailyTotals(cur, r.from, r.to, f);
    const b = dailyTotals(prev, c.from, c.to, f);
    const n = dailyTotals(cur, r.from, r.to, () => 1);
    return {
      unit: "day",
      x: a.map((o) => dayLabel(o.date)),
      cur: a.map((o) => o.v),
      cmp: b.map((o) => o.v),
      extra: n.map((o) => o.v),
      cmpName: c.name,
    };
  }, [v, data]);

  /* ---------------- drawer ---------------- */
  const openInvoices = useCallback(
    (title, rows, note) =>
      setDrawer({
        title,
        sub: `${rows.length} invoices · ${branch?.name || ""}`,
        note,
        cols: INVOICE_COLS,
        rows: rows.slice().sort((a, b) => (b.date + clock(b.mins)).localeCompare(a.date + clock(a.mins))).map(invoiceRow),
      }),
    [branch]
  );
  const drawerCsv = (d) => {
    downloadCsv(
      `${d.title}.csv`.replace(/[\\/:*?"<>|]/g, "-"),
      d.cols.map((c) => c.t),
      d.rows.map((row) => d.cols.map((c) => row[c.k]))
    );
    showToast("List exported");
  };

  /* ---------------- handlers ---------------- */
  const refresh = () => {
    setReload((x) => x + 1);
    showToast("Figures refreshed");
  };
  const pickPeriod = (id) => BRANCH_PERIODS.some((p) => p.id === id) && setPeriod(id);
  const toggleTable = (k) => setTables((t) => ({ ...t, [k]: !t[k] }));
  const exportCsv = () => {
    if (!trend) return;
    downloadCsv(
      `axoone-${String(branch?.name || "branch").toLowerCase().replace(/\s+/g, "-")}-${period}.csv`,
      [trend.unit === "hour" ? "Hour" : "Date", "Sales", "Comparison", "Invoices"],
      trend.x.map((s, i) => [s, (trend.cur[i] || 0).toFixed(2), (trend.cmp[i] || 0).toFixed(2), trend.extra[i]])
    );
    showToast("CSV downloaded");
  };

  useDashKeys({ onPeriod: pickPeriod, onRefresh: refresh, onEscape: () => setDrawer(null) });

  /* ---------------- render ---------------- */
  const r = v?.r || rangeFor(period);
  const c = v?.c || cmpRange(r, cmp);
  const title = branch?.name || "Branch dashboard";
  const sub = !guid
    ? "Pick a branch from the store menu in the header"
    : period === "today"
      ? longDate(r.to) + (v?.firstMins != null ? ` · trading since ${clock(v.firstMins)}` : "")
      : `${cap(r.name)} · ${r.n === 1 ? longDate(r.to) : dayLabel(r.from) + " → " + dayLabel(r.to)}`;

  // payment mix
  const mix = v ? paymentMix(v.cur, vatOn) : [];
  const mixTotal = mix.reduce((a, m) => a + m.v, 0);

  // top items
  const itemRows = (v?.items || [])
    .slice()
    .sort((a, b) => b[itemSort === "qty" ? "qty" : "value"] - a[itemSort === "qty" ? "qty" : "value"])
    .slice(0, 7);
  const itemMax = itemRows[0] ? itemRows[0][itemSort === "qty" ? "qty" : "value"] : 1;

  // customers / cashiers
  const custRows = v ? rankBy(v.cur, (x) => x.customer, v.f).slice(0, 6) : [];
  const cashierRows = v ? rankBy(v.cur, (x) => x.cashier, v.f).slice(0, 6) : [];
  const cashierMax = cashierRows[0]?.v || 1;

  // needs attention
  const pending = v ? v.cur.filter((x) => x.status === 1) : [];
  const credit = v ? v.cur.filter((x) => x.payType === 4) : [];
  const outs = v ? v.pio.filter((p) => p.out) : [];
  const queue = [
    {
      key: "pending",
      sev: pending.length ? "warnx" : "info",
      ic: "doc",
      t: pending.length ? "Bills still pending" : "No pending bills",
      s: pending.length ? "raised but not completed" : "every bill is completed",
      n: pending.length ? `${pending.length} bills` : "All clear",
    },
    {
      key: "credit",
      sev: "info",
      ic: "card",
      t: "Sold on credit",
      s: `${credit.length} invoices on account ${r.name}`,
      n: compact(sumOf(credit, v ? v.f : () => 0)),
    },
    {
      key: "payout",
      sev: "info",
      ic: "cash",
      t: "Cash paid out of the drawer",
      s: `${outs.length} pay-outs ${r.name}`,
      n: compact(v?.payOut || 0),
    },
    { key: "ar90", sev: "crit", ic: "clock", t: "Overdue receivables past 90 days", s: "customer ageing", todo: true },
    { key: "oos", sev: "crit", ic: "box", t: "Items out of stock with open orders", s: "sales blocked on the counter", todo: true },
    { key: "grn", sev: "warnx", ic: "truck", t: "Goods receipts awaiting purchase invoice", s: "unbilled GRNs", todo: true },
    { key: "dn", sev: "warnx", ic: "doc", t: "Delivery notes not yet invoiced", s: "goods gone, no bill raised", todo: true },
    { key: "limit", sev: "warnx", ic: "card", t: "Customers over their credit limit", s: "POS will ask for approval", todo: true },
    { key: "reorder", sev: "warnx", ic: "box", t: "Items below reorder level", s: "fast movers first", todo: true },
    { key: "quote", sev: "info", ic: "quote", t: "Quotations expiring within 3 days", s: "worth chasing today", todo: true },
    { key: "po", sev: "info", ic: "po", t: "Purchase orders overdue from supplier", s: "promised date passed", todo: true },
    { key: "wast", sev: "info", ic: "warn", t: "Damage & wastage awaiting approval", s: "posted by counter staff", todo: true },
  ];
  const onQueue = (x) => {
    if (x.key === "pending") openInvoices("Bills still pending", pending);
    else if (x.key === "credit") openInvoices("Sold on credit", credit);
    else if (x.key === "payout")
      setDrawer({
        title: "Cash paid out",
        sub: `${outs.length} pay-outs · ${branch?.name || ""}`,
        cols: [
          { k: "when", t: "Date / time" },
          { k: "acc", t: "Account" },
          { k: "nar", t: "Narration" },
          { k: "amt", t: "Amount", num: true },
        ],
        rows: outs.map((p) => ({
          when: dayLabel(p.date) + (p.mins !== null ? " " + clock(p.mins) : ""),
          acc: p.account || "—",
          nar: p.narration || "—",
          amt: moneyDp(p.amount),
        })),
      });
  };

  // live activity - invoices and pay in / out, newest first
  const feed = v
    ? [
        ...v.cur.map((x) => ({
          k: "i" + x.id,
          tg: x.payType === 4 ? "CRED" : "SALE",
          cls: "sale",
          doc: x.billNo || "—",
          who: x.customer || x.cashier || x.counter,
          amt: v.f(x),
          date: x.date,
          mins: x.mins,
          inv: x,
        })),
        ...v.pio.map((p) => ({
          k: "p" + p.id,
          tg: p.out ? "PAY" : "RCPT",
          cls: p.out ? "pay" : "rcpt",
          doc: p.out ? "Pay out" : "Pay in",
          who: p.account || p.narration,
          amt: p.amount,
          date: p.date,
          mins: p.mins,
        })),
      ]
        .sort((a, b) => (b.date + clock(b.mins)).localeCompare(a.date + clock(a.mins)))
        .slice(0, 20)
    : [];
  const today = dayjs().format("YYYY-MM-DD");

  const drawerEl = (
    <Drawer
      data={drawer}
      onClose={() => setDrawer(null)}
      onCsv={drawerCsv}
      primary={
        <button type="button" className="axd-btn primary" onClick={() => setDrawer(null)}>
          Done
        </button>
      }
    />
  );

  return (
    <DashScreen
      title={title}
      sub={sub}
      updated={updated}
      loading={loading}
      error={error}
      drawer={drawerEl}
      toast={toast}
      keys={[
        ["T", "Today"],
        ["W", "Week"],
        ["M", "Month"],
        ["R", "Refresh"],
        ["Esc", "Close"],
      ]}
      status={`${branch?.name || "No branch"} · ${vatOn ? "incl. VAT" : "excl. VAT"} · ${r.n} ${r.n === 1 ? "day" : "days"}`}
    >
      <PeriodBar
        periods={BRANCH_PERIODS}
        period={period}
        onPeriod={setPeriod}
        cmp={cmp}
        onCmp={setCmp}
        vat={vatOn}
        onVat={setVatOn}
        vatLabel="Show amounts with VAT"
        onRefresh={refresh}
        onCsv={exportCsv}
        onPrint={() => window.print()}
      />

      {/* ============ KPI ROW ============ */}
      <section className="axd-kpirow">
        <Hero
          label={`Net sales · ${r.name}`}
          value={money(v?.sales || 0)}
          loading={!v && loading}
          prev={v ? { cur: v.sales, prev: v.psales } : undefined}
          cmpName={c.name}
          spark={<Sparkline vals={v?.spark || []} w={256} h={40} />}
          footLabel={r.live ? "Target to now" : "Target"}
          foot={<b>—</b>}
        />
        <div className="axd-tiles">
          <Tile label="Gross profit" todo needs="needs item cost on the sales report" />
          <Tile
            label="Invoices"
            value={money(v?.inv || 0)}
            delta={v ? <Delta cur={v.inv} prev={v.pinv} small /> : null}
            sub={`avg bill ${moneyDp(v && v.inv ? v.sales / v.inv : 0, currfmt)}`}
            spark={<Sparkline vals={v?.sparkInv || []} />}
            onClick={() => v && openInvoices(`Invoices · ${r.name}`, v.cur)}
          />
          <Tile label="Collections" todo needs="needs a receipts summary" />
          <Tile label="Purchases" todo needs="needs a purchases summary" />
          <Tile label="Sales returns" todo needs="needs returns in the dashboard feed" />
          <Tile
            label="Cash in hand"
            value={compact(v?.cashHand || 0)}
            delta={v ? <Delta cur={v.cashSales} prev={v.pcash} small /> : null}
            sub="cash sales + pay in − pay out"
            spark={<Sparkline vals={v?.sparkCash || []} />}
            onClick={() =>
              v &&
              openInvoices(
                `Cash sales · ${r.name}`,
                v.cur.filter((x) => x.cash),
                `Cash sales ${moneyDp(v.cashSales)} + pay in ${moneyDp(v.payIn)} − pay out ${moneyDp(v.payOut)}. The opening float is not included.`
              )
            }
          />
        </div>
      </section>

      {/* ============ MAIN GRID ============ */}
      <section className="axd-g12">
        {/* trend */}
        <Card
          className="c8"
          title={trend?.unit === "hour" ? "Sales through the day" : "Sales day by day"}
          note={trend?.noTimes ? "last 12 days · invoice times not sent" : trend?.unit === "hour" ? "hourly" : `${trend?.x.length || 0} days`}
          right={
            <MiniBtn on={tables.trend} onClick={() => toggleTable("trend")}>
              {tables.trend ? "Chart" : "Table"}
            </MiniBtn>
          }
          bodyClass="col"
        >
          {!trend ? (
            <div className="axd-nochart axd-empty">{guid ? "Loading…" : "No branch selected."}</div>
          ) : tables.trend ? (
            <DataTable
              cols={[
                { k: "x", t: trend.unit === "hour" ? "Hour" : "Date" },
                { k: "a", t: "Sales", num: true },
                { k: "b", t: trend.cmpName, num: true },
                { k: "d", t: "Diff", num: true },
                { k: "n", t: "Invoices", num: true },
              ]}
              rows={trend.x.map((s, i) => {
                const df = (trend.cur[i] || 0) - (trend.cmp[i] || 0);
                return {
                  x: s,
                  a: money(trend.cur[i]),
                  b: trend.cmp.length ? money(trend.cmp[i]) : "—",
                  d: trend.cmp.length ? (
                    <span style={{ color: df >= 0 ? "var(--money)" : "var(--danger)" }}>
                      {(df >= 0 ? "+" : "") + money(df)}
                    </span>
                  ) : (
                    "—"
                  ),
                  n: trend.extra[i],
                };
              })}
            />
          ) : (
            <BarTrend x={trend.x} cur={trend.cur} cmp={trend.cmp} extra={trend.extra} cmpName={trend.cmpName} />
          )}
          {trend && (
            <div className="axd-legend">
              <span className="li">
                <span className="sw" style={{ background: "var(--accent)" }} />
                {trend.noTimes ? "Last 12 days" : cap(r.name.replace(/^the /, ""))}
              </span>
              {trend.cmp.length > 0 && (
                <span className="li">
                  <span className="ln" style={{ background: "var(--ghost)" }} />
                  {trend.cmpName}
                </span>
              )}
              {trend.noTimes ? (
                <span className="li">
                  hourly split <Todo />
                </span>
              ) : (
                <span className="li" style={{ color: "var(--ink-3)" }}>
                  hover for the exact figure
                </span>
              )}
            </div>
          )}
        </Card>

        {/* needs attention */}
        <Card
          className="c4"
          title="Needs attention"
          note={`${queue.filter((x) => !x.todo).length} live · ${queue.filter((x) => x.todo).length} waiting on server`}
        >
          <Queue items={queue} onPick={onQueue} />
        </Card>

        {/* payment mix */}
        <Card
          className="c4"
          title="How customers paid"
          right={
            <MiniBtn on={tables.pay} onClick={() => toggleTable("pay")}>
              {tables.pay ? "Chart" : "Table"}
            </MiniBtn>
          }
        >
          {tables.pay ? (
            <DataTable
              cols={[
                { k: "n", t: "Tender" },
                { k: "v", t: "Amount", num: true },
                { k: "p", t: "Share", num: true },
              ]}
              rows={[
                ...mix.map((m) => ({ n: m.n, v: moneyDp(m.v), p: fmtPct(pct(m.v, mixTotal)) })),
                { __total: true, n: "Total", v: moneyDp(mixTotal), p: "100.0%" },
              ]}
            />
          ) : (
            <>
              <StackBar parts={mix} />
              <div className="axd-stkey">
                {mix.map((m) => (
                  <span className="k" key={m.n}>
                    <span className="sw" style={{ background: m.c }} />
                    <span className="kn">{m.n}</span>
                    <span className="kv">{money(m.v)}</span>
                    <span className="kp">{fmtPct(pct(m.v, mixTotal), 0)}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* top items */}
        <Card
          className="c4"
          title="Top items"
          right={<Seg small items={ITEM_SORTS} value={itemSort} onChange={setItemSort} disabledIds={["margin"]} />}
        >
          <div className="axd-blist">
            {itemRows.length ? (
              itemRows.map((x) => (
                <button
                  type="button"
                  className="axd-brow"
                  key={x.name}
                  onClick={() =>
                    setDrawer({
                      title: x.name,
                      sub: `${cap(r.name)} · ${branch?.name || ""}`,
                      cols: [
                        { k: "d", t: "Date" },
                        { k: "q", t: "Qty", num: true },
                        { k: "v", t: "Value", num: true },
                      ],
                      rows: Object.keys(x.days)
                        .sort()
                        .reverse()
                        .map((d) => ({ d: dayLabel(d), q: x.days[d].qty, v: moneyDp(x.days[d].value) })),
                    })
                  }
                >
                  <span>
                    <span className="nm">{x.name}</span>
                    <span className="track">
                      <i style={{ width: pct(itemSort === "qty" ? x.qty : x.value, itemMax).toFixed(1) + "%" }} />
                    </span>
                  </span>
                  <span className="v">
                    {itemSort === "qty" ? money(x.qty) : compact(x.value)}
                    <small>{itemSort === "qty" ? "units" : `${money(x.qty)} u`}</small>
                  </span>
                </button>
              ))
            ) : (
              <div className="axd-empty">No item sales in this period.</div>
            )}
          </div>
        </Card>

        {/* top customers */}
        <Card
          className="c4"
          title="Top customers"
          right={
            <span className="note" style={{ fontSize: 11, color: "var(--ink-3)" }}>
              credit exposure <Todo />
            </span>
          }
        >
          <div className="axd-blist">
            {custRows.length ? (
              custRows.map((x) => (
                <button
                  type="button"
                  className="axd-brow"
                  key={x.name}
                  onClick={() => openInvoices(x.name, v.cur.filter((i) => i.customer === x.name))}
                >
                  <span>
                    <span className="nm">{x.name}</span>
                    <span className="track">
                      <i style={{ width: pct(x.v, custRows[0].v).toFixed(1) + "%" }} />
                    </span>
                  </span>
                  <span className="v">
                    {compact(x.v)}
                    <small>
                      {x.n} {x.n === 1 ? "invoice" : "invoices"}
                    </small>
                  </span>
                </button>
              ))
            ) : (
              <div className="axd-empty">No sales to named customers in this period.</div>
            )}
          </div>
        </Card>

        {/* receivables */}
        <Card className="c4" title="Money owed to us">
          <Pending what="Receivables by age" needs="Not due / 31–60 / 61–90 / over 90 days, per branch." />
          <div className="axd-caption">Age bands</div>
          <div className="axd-stkey">
            {["Not due / 0–30 days", "31–60 days", "61–90 days", "Over 90 days"].map((n, i) => (
              <span className="k" key={n}>
                <span className="sw" style={{ background: `var(--age${i + 1})` }} />
                <span className="kn">{n}</span>
                <span className="kv">—</span>
              </span>
            ))}
          </div>
        </Card>

        {/* salesmen */}
        <Card
          className="c4"
          title="Salesman vs target"
          right={
            <span className="note" style={{ fontSize: 11, color: "var(--ink-3)" }}>
              targets <Todo />
            </span>
          }
        >
          {cashierRows.length ? (
            cashierRows.map((x) => (
              <button
                type="button"
                className="axd-mrow"
                key={x.name}
                onClick={() => openInvoices(x.name, v.cur.filter((i) => i.cashier === x.name))}
              >
                <span>
                  <span className="nm">
                    <span className="av">{initials(x.name)}</span>
                    {x.name}
                    <em>· {x.n} inv</em>
                  </span>
                  <span className="axd-meter">
                    <i style={{ width: pct(x.v, cashierMax).toFixed(1) + "%" }} />
                  </span>
                </span>
                <span className="pct">
                  {fmtPct(pct(x.v, v.sales), 0)}
                  <small>{compact(x.v)} of sales</small>
                </span>
              </button>
            ))
          ) : (
            <div className="axd-empty">No sales in this period.</div>
          )}
        </Card>

        {/* stock */}
        <Card className="c4" title="Stock health">
          <Pending what="Stock position" needs="Stock on hand, cover, dead stock, negative stock, expiring batches, damage." />
        </Card>

        {/* purchase & payables */}
        <Card className="c7" title="Purchase & supplier position">
          <Pending
            what="Purchases and payables"
            needs="Purchases, paid to suppliers, open purchase orders, purchase returns and what we owe by age."
          />
        </Card>

        {/* activity */}
        <Card className="c5" title="Live activity" note={`last ${feed.length} documents`}>
          <div className="axd-feed">
            {feed.length ? (
              feed.map((x) => (
                <button
                  type="button"
                  className="axd-frow"
                  key={x.k}
                  onClick={() =>
                    x.inv
                      ? setDrawer({
                          title: `Invoice ${x.inv.billNo}`,
                          sub: `${dayLabel(x.inv.date)} ${clock(x.inv.mins)} · ${branch?.name || ""}`,
                          cols: [
                            { k: "m", t: "Field" },
                            { k: "v", t: "Value", num: true },
                          ],
                          rows: [
                            { m: "Gross total", v: moneyDp(x.inv.gross) },
                            { m: "Discount", v: moneyDp(x.inv.discount) },
                            { m: "VAT", v: moneyDp(x.inv.vat) },
                            { m: "Net total", v: moneyDp(x.inv.net) },
                            { m: "Cash", v: moneyDp(x.inv.cash) },
                            { m: "Card", v: moneyDp(x.inv.card) },
                            { m: "Payment", v: payName(x.inv.payType) },
                            { m: "Cashier", v: x.inv.cashier || "—" },
                            { m: "Counter", v: x.inv.counter || "—" },
                            { m: "Customer", v: x.inv.customer || "—" },
                          ],
                        })
                      : showToast(`${x.doc} · ${moneyDp(x.amt)}`)
                  }
                >
                  <span className={"axd-tg " + x.cls}>{x.tg}</span>
                  <span className="fn">
                    <b style={{ fontWeight: 600 }}>{x.doc}</b> <em>{x.who}</em>
                  </span>
                  <span className="fv">{money(x.amt)}</span>
                  <span className="ft">{x.date === today && x.mins !== null ? clock(x.mins) : dayLabel(x.date)}</span>
                </button>
              ))
            ) : (
              <div className="axd-empty">Nothing posted in this period yet.</div>
            )}
          </div>
        </Card>
      </section>
    </DashScreen>
  );
};

export default Dashboard;
